import type { ChartTheme } from './chartTheme'
import type { LabelledMilestone } from './milestoneLabelPlacement'

const SAME_AGE_LABEL_SEPARATOR = '/'

/** Between the line's top and the chip hanging from it. None: the chip covers it. */
const LABEL_DROP = 0

/**
 * Where the line's top end sits, in canvas points below the top of the grid.
 * Only enough to keep the chip that hangs from it off the edge.
 */
const LINE_TOP_DROP = 2

/** Between the chip's near edge and the line, so the two do not touch. */
const LINE_GAP = 2

/**
 * How much further down a chip in the row below sits, in canvas points. A chip
 * drops a row only where neither side of its line had room — see
 * `withLabelPlacement`.
 */
const ROW_STEP = 22

/** Vertical, horizontal — enough that the rule is not against the word. */
const CHIP_PADDING = [3, 7]

/** The one edge the chip keeps. See the label's shadow. */
const BOTTOM_RULE_WIDTH = 1

// Compact a label for use in a combined same-age marker — the rotated
// markLine label has limited room, so collapse any whitespace into
// PascalCase to keep the joined string readable.
const toCompactLabel = (label: string): string =>
  label
    .trim()
    .split(/\s+/)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join('')

type RenderableMilestone = LabelledMilestone & { merged?: boolean }

/**
 * Milestones at the same age would render as overlapping markLines with
 * rotated labels stacked on top of each other — neither readable. Collapse
 * them into one entry with a compacted joined label; the merged flag tells
 * the renderer to use a neutral (black) color so neither original severity
 * color implies the merged marker means just one thing.
 */
const mergeByAge = (milestones: LabelledMilestone[]): RenderableMilestone[] => {
  const groups = new Map<string, LabelledMilestone[]>()
  for (const m of milestones) {
    const list = groups.get(m.age) ?? []
    list.push(m)
    groups.set(m.age, list)
  }
  return [...groups.values()].map(group => {
    if (group.length === 1) return group[0]
    return {
      ...group[0],
      label: group
        .map(m => toCompactLabel(m.label))
        .join(SAME_AGE_LABEL_SEPARATOR),
      merged: true,
    }
  })
}

const lineColor = (theme: ChartTheme, m: RenderableMilestone): string => {
  if (m.merged) return theme.markLine.mergedLineColor
  return m.severity === 'danger'
    ? theme.markLine.dangerLineColor
    : theme.markLine.lineColor
}

const labelColor = (theme: ChartTheme, m: RenderableMilestone): string => {
  if (m.merged) return theme.markLine.mergedLabelColor
  return m.severity === 'danger'
    ? theme.markLine.dangerLabelColor
    : theme.markLine.labelColor
}

export const buildMarkLine = (
  milestones: LabelledMilestone[],
  theme: ChartTheme
): object | undefined => {
  /*
   * Two points rather than one age, because the line stops short of the top.
   *
   * The one-age form expands to the grid's full height, and ECharts offers no
   * way to end a mark line part way up it. What it does offer is a pixel `y` on
   * an endpoint, which overrides the coordinate it computed — so the pair is
   * the axis floor and a point `CHIP_BAND` below the top of the grid.
   *
   * `-Infinity` on the value dimension is ECharts' own shorthand for "the end
   * of the axis", and it is what the one-age form expands to internally. Both
   * ends carry it so the pair is still recognised as a single-dimension line
   * and is not filtered out as off-grid.
   *
   * The line's style and its label hang off the far point, which ECharts merges
   * into the line itself.
   */
  const milestoneData = mergeByAge(milestones).map(m => [
    { xAxis: m.age, yAxis: -Infinity },
    {
      xAxis: m.age,
      yAxis: -Infinity,
      y: theme.gridTop + LINE_TOP_DROP + (m.labelRow ?? 0) * ROW_STEP,
      lineStyle: {
        type: 'solid',
        width: 1,
        color: lineColor(theme, m),
      },
      label: {
        show: true,
        formatter: m.label,
        // `insideEndBottom`: for a vertical line the end is the top, so the
        // chip hangs from it and covers the line from there down.
        position: 'insideEndBottom' as const,
        rotate: 0,
        // ECharts right-aligns against the line, which puts a label near the
        // start of the axis off the canvas. An explicit `align` wins, and a
        // negative x distance moves the anchor with it.
        ...(m.labelSide === 'right'
          ? {
              align: 'left' as const,
              distance: [-LINE_GAP, LABEL_DROP],
            }
          : { distance: [LINE_GAP, LABEL_DROP] }),
        color: labelColor(theme, m),
        fontFamily: theme.fontFamily,
        fontSize: theme.fontSize - 1,
        // A chip because it can be pressed: a bare word over a plot reads as a
        // caption, and a caption does not invite a tap.
        backgroundColor: theme.markLine.labelBackgroundColor,
        padding: CHIP_PADDING,
        /*
         * The rule under the chip, drawn as a hard shadow: ECharts has no
         * per-side border on a label, and all four boxed the word in. An
         * unblurred shadow offset straight down shows only along the bottom
         * edge — but the fill must stay opaque, or it tints the whole chip.
         */
        shadowColor: theme.markLine.labelBackgroundColor,
        shadowBlur: 0,
        shadowOffsetX: 0,
        shadowOffsetY: BOTTOM_RULE_WIDTH,
      },
    },
  ])

  if (milestoneData.length === 0) {
    return undefined
  }

  return {
    // Not silent: ECharts reports a `click` only on a mark line that takes
    // events, and the chip opens the year it names. `EChart` tells that tap
    // apart from one on the plot, which selects the nearest year instead.
    silent: false,
    // Taking events also hands ECharts a selected state, which emboldens the
    // label — so the tap that opens a year would leave its chip bold.
    emphasis: { disabled: true },
    symbol: 'none',
    data: milestoneData,
  }
}
